import { forwardRef, useEffect, useMemo, useState } from "react";
import { ADDITIONAL_PERSONAL_DATA_VALIDATION_ENDPOINT } from "../../api/endpoints";
import { submitAdvancedPersonalData } from "../../api/forms/personal-data";
import {
    ActivityLevel,
    AdvancedPersonalData,
} from "../../api/forms/personal-data/types";
import { ExtendedBooleanText, Nullable } from "../../api/types";
import { fetcher } from "../../api/utils";
import useDataInputForm, {
    booleanOptions,
    FORM_IDS,
} from "../../hooks/useDataInputForm";
import useImperativeFormSubmit from "../../hooks/useImperativeFormSubmit";
import { DataInputFormField, FormHandle, InputTypes } from "../../types";
import { generateSelectOptions, onFormValuesChange } from "../../utils";
import Error from "../Error";
import DataInputGenericForm, {
    DataInputGenericFormProps,
} from "./DataInputGenericForm";
import useTestId from "../../hooks/useTestId";

const ACTIVE_LEVEL_TEXTS = {
    [ActivityLevel.MILDLY_ACTIVE]: "Mildly active",
    [ActivityLevel.NOT_ACTIVE]: "Not active",
    [ActivityLevel.VERY_ACTIVE]: "Very active",
    [ActivityLevel.UNKNOWN]: "Unknown",
};

export default forwardRef<
    FormHandle<Nullable<AdvancedPersonalData>> | null,
    {
        triggerPrespirometryUpdate: () => Promise<void>;
    }
>(function AdvancedPersonalDataForm(
    props,
    ref: React.ForwardedRef<FormHandle<Nullable<AdvancedPersonalData>> | null>
) {
    const testId = useTestId();
    const { formData, loading, error, setFromData } = useDataInputForm({
        formId: FORM_IDS.ADVANCED_PERSONAL_DATA,
    });

    const [formValues, setFormValues] = useState<
        AdvancedPersonalData | null | undefined
    >();
    const [validationErrors, setValidationErrors] =
        useState<Partial<Record<keyof AdvancedPersonalData, string>>>();

    useEffect(() => {
        if (formData) {
            setFormValues(formData as AdvancedPersonalData);
        }
    }, [formData]);

    const { submissionLoading } = useImperativeFormSubmit<AdvancedPersonalData>(
        {
            formValues,
            formData,
            ref,
            onSubmitSuccess: setFromData,
            submitFunction: submitAdvancedPersonalData,
        }
    );

    type FormFields =
        DataInputGenericFormProps<AdvancedPersonalData>["formFields"];

    const formFields = useMemo<FormFields>((): FormFields => {
        return !formData || !formValues
            ? []
            : [
                  {
                      id: "smoker",
                      label: "Smoking",
                      value: formValues.smoker,
                      valueToDisplay: formValues.smoker,
                      originalValue: formData.smoker,
                      type: InputTypes.SELECT,
                      options: [
                          ...booleanOptions,
                          {
                              id: ExtendedBooleanText.UNKNOWN,
                              value: ExtendedBooleanText.UNKNOWN,
                              label: ExtendedBooleanText.UNKNOWN,
                          },
                      ],
                      required: false,
                      error: validationErrors?.smoker,
                  },
                  {
                      id: "active",
                      label: "Activity",
                      value: formValues.active,
                      valueToDisplay: formValues.active,
                      originalValue: formData.active,
                      type: InputTypes.SELECT,
                      options: generateSelectOptions({
                          enum: ActivityLevel,
                          texts: ACTIVE_LEVEL_TEXTS,
                      }),
                      required: false,
                      error: validationErrors?.active,
                  },
                  {
                      id: "race",
                      label: "Race",
                      value: formValues.race,
                      valueToDisplay: formValues.race,
                      originalValue: formData.race,
                      type: InputTypes.SELECT,

                      options: formData.all_race_types,
                      required: false,
                      error: validationErrors?.race,
                  },
              ];
    }, [formData, formValues, validationErrors]);

    if (error) {
        return <Error />;
    }
    const validateOnFormValuesChange = async ({
        value,
        fieldSettings,
    }: {
        value: Nullable<string | number>;
        fieldSettings: DataInputFormField;
    }) => {
        const validationRes = await fetcher(
            ADDITIONAL_PERSONAL_DATA_VALIDATION_ENDPOINT,
            {
                method: "POST",
                data: { [fieldSettings.id]: value },
            },
            true
        );
        const error = validationRes?.[fieldSettings.id]?.error_message;
        const hasError = Boolean(!validationRes || error);
        if (hasError) {
            setValidationErrors((prev) => ({
                ...prev,
                [fieldSettings.id]: error,
            }));
        } else {
            setValidationErrors((prev) => ({
                ...prev,
                [fieldSettings.id]: undefined,
            }));
        }
        onFormValuesChange({
            value,
            fieldSettings,
            setFormValues,
            noZeroValues: true,
        });

        const needToTriggerPrespirometryUpdate = ["race"].includes(
            fieldSettings.id
        );

        if (needToTriggerPrespirometryUpdate) {
            // update data
            await submitAdvancedPersonalData({
                testId: testId!,
                values: { [fieldSettings.id]: value as string },
            });
            // trigger prespirometry update
            await props.triggerPrespirometryUpdate();
        }
    };

    return (
        <DataInputGenericForm
            formTitle="Additional Personal Data"
            formFields={formFields}
            formData={formValues}
            onFromValuesChange={validateOnFormValuesChange}
            loading={loading || submissionLoading}
        />
    );
});
